import {
  signal,
  computed,
  collection,
  For,
  Show,
  Motion,
  onMount,
  onCleanup,
  spring,
} from "zyra";
import type { SiteState } from "../app/site-state";
import { setupExperience } from "./experience";
interface Task {
  id: string;
  title: string;
  done: boolean;
  category: string;
}

export function LandingPage(props: { site: SiteState }) {
  const { router } = props.site;
  const tasks = collection<Task>("tasks", {
    key: "id",
    database: "zyra-docs-playground",
    local: "indexeddb",
    validate: (value): value is Task =>
      !!value &&
      typeof value === "object" &&
      typeof (value as Task).id === "string" &&
      typeof (value as Task).title === "string" &&
      (value as Task).title.trim().length > 0 &&
      (value as Task).title.length <= 200 &&
      typeof (value as Task).done === "boolean" &&
      typeof (value as Task).category === "string",
  });

  const filter = signal("all"),
    tab = signal("workspace"),
    draft = signal("");
  const message = signal(""),
    busy = signal(false),
    copied = signal(false);
  const online = signal(navigator.onLine),
    motionTab = signal("spring"),
    stiffness = signal(180);
  const visible = computed(() =>
    tasks.items.filter(
      (task) =>
        filter.value === "all" ||
        (filter.value === "done" ? task.done : !task.done),
    ),
  );
  const completed = computed(
    () => tasks.items.filter((task) => task.done).length,
  );
  let setupRuns = 0,
    springPosition = 0,
    direction = 1;
  let activeSpring: ReturnType<typeof spring> | undefined;
  let copyTimer: ReturnType<typeof setTimeout> | undefined;

  const source = `import { collection, Motion, For } from "zyra";

const tasks = collection<Task>("tasks", {
  key: "id",
  local: "indexeddb",
  sync: { endpoint: "/api/sync" }
});

function App() {
  return (
    <For each={tasks.items} key="id">
      {(task) => (
        <Motion.article
          enter={{ opacity: 0, y: 16 }}
          hover={{ scale: 1.02 }}
        >
          {task.title}
        </Motion.article>
      )}
    </For>
  );
}`;

  async function action(work: () => Promise<unknown>) {
    message.value = "";
    try {
      await work();
    } catch (error) {
      message.value = error instanceof Error ? error.message : String(error);
    }
  }
  async function addTask(event: Event) {
    event.preventDefault();
    const title = draft.value.trim();
    if (!title || busy.value) return;
    busy.value = true;
    await action(async () => {
      await tasks.insert({
        id: crypto.randomUUID(),
        title,
        done: false,
        category: "Personal",
      });
      draft.value = "";
    });
    busy.value = false;
  }

  function replaySpring() {
    const track = document.querySelector<HTMLElement>(".spring-track");
    const dot = document.querySelector<HTMLElement>(".spring-dot");
    if (!track || !dot) return;
    activeSpring?.cancel();
    const distance = Math.max(0, Math.min(150, (track.clientWidth - 110) / 2));
    const target = distance * direction;
    direction *= -1;
    activeSpring = spring(
      springPosition,
      target,
      (value) => {
        springPosition = value;
        dot.style.transform = `translateX(${value}px)`;
      },
      { stiffness: stiffness.value, damping: 14 },
    );
  }
  async function copyCommand() {
    await action(async () => {
      await navigator.clipboard.writeText("npm run dev");
      copied.value = true;
      clearTimeout(copyTimer);
      copyTimer = setTimeout(() => (copied.value = false), 1800);
    });
  }
  function Arrow() {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 12h14M13 6l6 6-6 6"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );
  }
  function Logo() {
    return (
      <span class="logo">
        <svg viewBox="0 0 30 30" fill="none" aria-hidden="true">
          <path
            d="M6 7h18L6 23h18M6 15h18"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linejoin="round"
          />
        </svg>
        zyra<span class="logo-period">.</span>
      </span>
    );
  }

  onMount(() => {
    let canceled = false;
    void (async () => {
      await tasks.ready;
      if (canceled) return;
      try {
        if (
          tasks.status.writable &&
          !localStorage.getItem("zyra-docs-seeded") &&
          !tasks.items.length
        ) {
          for (const task of [
            {
              title: "Build something that moves people",
              category: "Creative",
              done: false,
            },
            {
              title: "Sweat the small interactions",
              category: "Design",
              done: false,
            },
            {
              title: "Try this without an internet connection",
              category: "Experiment",
              done: false,
            },
            {
              title: "Find a framework with a little more feeling",
              category: "Creative",
              done: true,
            },
          ]) {
            if (canceled) return;
            await tasks.insert({ id: crypto.randomUUID(), ...task });
          }
          localStorage.setItem("zyra-docs-seeded", "1");
        }
      } catch (error) {
        message.value = String(error);
      }
    })().catch((error) => {
      if (!canceled) message.value = String(error);
    });
    return () => {
      canceled = true;
    };
  });
  setupRuns++;
  let root!: HTMLElement;
  const network = () => (online.value = navigator.onLine);
  window.addEventListener("online", network);
  window.addEventListener("offline", network);
  onMount(() => setupExperience(root));
  onCleanup(() => {
    void tasks.close();
    activeSpring?.cancel();
    clearTimeout(copyTimer);
    window.removeEventListener("online", network);
    window.removeEventListener("offline", network);
  });
  return (
    <div
      class="landing-page site"
      ref={(element: HTMLElement) => (root = element)}
    >
      <a
        class="skip-link"
        href="#playground"
        onClick={() =>
          queueMicrotask(() => {
            const target = document.querySelector<HTMLElement>("#playground");
            if (target) {
              target.tabIndex = -1;
              target.focus({ preventScroll: true });
            }
          })
        }
      >
        Skip to playground
      </a>
      <div class="reading-progress" aria-hidden="true" />
      <header class="site-header">
        <a href="#" aria-label="Zyra home">
          <Logo />
        </a>
        <nav aria-label="Main navigation">
          <a href="#why-zyra">Why Zyra</a>
          <a href="#motion">Motion</a>
          <router.Link href="/docs">Documentation</router.Link>
          <router.Link href="/docs/ui">UI library</router.Link>
        </nav>
        <router.Link class="nav-cta" href="/docs/installation">
          Start building <Arrow />
        </router.Link>
      </header>

      <main id="content" tabIndex={-1}>
        <section class="hero section-wrap" aria-labelledby="hero-title">
          <div class="hero-grid" aria-hidden="true" />
          <div class="hero-copy">
            <div class="release-pill reveal">
              <span class="status-dot" /> INTRODUCING ZYRA{" "}
              <span class="release-version">v0.1 release candidate</span>
            </div>
            <h1 id="hero-title" class="reveal" data-delay="70">
              Less friction.
              <br />
              More{" "}
              <span class="feeling-word">
                feeling
                <svg viewBox="0 0 430 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 16C100 1 293 0 425 13"
                    stroke="currentColor"
                    stroke-width="3"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
              .
            </h1>
            <p class="hero-description reveal" data-delay="140">
              A frontend framework for interfaces that feel alive.
              <br class="desktop-break" /> Fine-grained reactivity. Native
              motion. Offline by default.
            </p>
            <div class="hero-actions reveal" data-delay="210">
              <router.Link
                class="button button-lime"
                href="/docs/installation"
                data-magnetic="true"
              >
                Start building <Arrow />
              </router.Link>
              <a class="text-link" href="#playground">
                Explore the playground <span>↗</span>
              </a>
            </div>
            <div class="hero-footnote reveal" data-delay="280">
              <span class="mini-code">{"</>"}</span> TypeScript & TSX{" "}
              <span class="separator">/</span> Independent by design
            </div>
          </div>
          <div class="hero-art reveal" data-delay="130">
            <div class="art-coordinates">
              <span>EXPERIENCE / 001</span>
              <span>REACTIVE FORM</span>
            </div>
            <div class="sculpture" aria-hidden="true">
              <div class="sculpture-shadow" />
              <div class="orbit-object">
                {Array.from({ length: 36 }, (_, i) => (
                  <div class="orbit-ring" style={{ "--i": i }} />
                ))}
              </div>
              <span class="orbital-point point-one" />
              <span class="orbital-point point-two" />
            </div>
            <div class="floating-tag tag-motion">
              <span class="status-dot" /> motion, built in.
              <span class="tag-corner">↗</span>
            </div>
            <div class="floating-tag tag-code">
              <span class="code-purple">signal</span>
              <span class="code-muted">(</span>
              <span class="code-lime">possibility</span>
              <span class="code-muted">)</span>
              <span class="code-cursor" />
            </div>
            <div class="art-caption">
              <span class="crosshair">＋</span>
              <span>PRECISE BY NATURE. EXPRESSIVE BY DESIGN.</span>
              <span class="crosshair">＋</span>
            </div>
          </div>
          <div class="hero-bottom">
            <a href="#why-zyra" class="scroll-cue">
              <span class="scroll-icon">↓</span> SCROLL TO EXPLORE
            </a>
            <span>
              Built for the web.
              <span class="muted"> And the way it should feel.</span>
            </span>
            <span class="hero-index">01 — 04</span>
          </div>
        </section>

        <section id="why-zyra" class="principles section-wrap">
          <div class="section-top reveal">
            <span class="eyebrow">
              <span class="status-dot" /> A DIFFERENT FOUNDATION
            </span>
            <span class="section-number">01 / THE FRAMEWORK</span>
          </div>
          <div class="section-intro reveal">
            <h2>
              Everything you need.
              <br />
              <span class="muted">Nothing in your way.</span>
            </h2>
            <p>
              Stop stitching together the basics.
              <br />
              Start with one coherent model for state,
              <br class="desktop-break" /> movement, and durable data.
            </p>
          </div>
          <div class="feature-grid">
            <article class="feature-card reveal">
              <div class="feature-top">
                <span class="feature-icon reactive-icon">
                  <i />
                  <i />
                  <i />
                  <i />
                </span>
                <span>01</span>
              </div>
              <h3>Update precisely.</h3>
              <p>
                A signal changes. Its binding updates.
                <br />
                Your component doesn't start over.
              </p>
              <div class="feature-bottom">
                <span>FINE-GRAINED REACTIVITY</span>
                <span>↗</span>
              </div>
            </article>
            <article class="feature-card reveal" data-delay="90">
              <div class="feature-top">
                <span class="feature-icon motion-icon">
                  <i />
                  <i />
                  <i />
                </span>
                <span>02</span>
              </div>
              <h3>Move naturally.</h3>
              <p>
                Springs, entrances, and interactions.
                <br />
                Expression is part of the foundation.
              </p>
              <div class="feature-bottom">
                <span>MOTION-NATIVE UI</span>
                <span>↗</span>
              </div>
            </article>
            <article class="feature-card reveal" data-delay="180">
              <div class="feature-top">
                <span class="feature-icon offline-icon">
                  <i />
                  <i />
                  <i />
                </span>
                <span>03</span>
              </div>
              <h3>Keep going.</h3>
              <p>
                Local writes. Durable state.
                <br />A lost connection isn't a dead end.
              </p>
              <div class="feature-bottom">
                <span>OFFLINE-NATIVE DATA</span>
                <span>↗</span>
              </div>
            </article>
          </div>
        </section>

        <section id="motion" class="motion-section section-wrap">
          <div class="section-top reveal">
            <span class="eyebrow">
              <span class="status-dot" /> DON'T JUST LOOK. INTERACT.
            </span>
            <span class="section-number">02 / THE FEELING</span>
          </div>
          <div class="motion-layout">
            <div class="motion-copy reveal">
              <h2>
                Good motion
                <br />
                is <span class="serif-word">a feeling.</span>
              </h2>
              <p>
                Responsive. Fluid. A little playful.
                <br />
                The kind of detail that makes an interface
                <br class="desktop-break" /> feel like it was made for you.
              </p>
              <a class="text-link" href="#scroll-experience">
                Keep scrolling. There's more. <span>↓</span>
              </a>
              <div class="motion-note">
                <span class="status-dot" /> ZYRA MOTION / LIVE DEMO
              </div>
            </div>
            <div class="motion-playground reveal" data-delay="100">
              <div class="demo-toolbar">
                <div class="demo-tabs" role="group" aria-label="Motion demos">
                  <button
                    aria-pressed={motionTab.value === "spring"}
                    onClick={() => (motionTab.value = "spring")}
                  >
                    Spring
                  </button>
                  <button
                    aria-pressed={motionTab.value === "hover"}
                    onClick={() => (motionTab.value = "hover")}
                  >
                    Interaction
                  </button>
                  <button
                    aria-pressed={motionTab.value === "scroll"}
                    onClick={() => (motionTab.value = "scroll")}
                  >
                    Scroll
                  </button>
                </div>
                <span class="demo-live">
                  <span class="status-dot" /> LIVE
                </span>
              </div>
              <div
                class="demo-pane spring-pane"
                hidden={motionTab.value !== "spring"}
              >
                <div class="demo-grid" />
                <div class="spring-track">
                  <span class="track-end left" />
                  <span class="track-end right" />
                  <div class="spring-ghost" />
                  <div class="spring-dot">
                    <svg viewBox="0 0 30 30" fill="none" aria-hidden="true">
                      <path
                        d="M7 8h16L7 22h16"
                        stroke="currentColor"
                        stroke-width="2"
                      />
                    </svg>
                  </div>
                </div>
                <span class="demo-instruction">
                  A little physics. A lot of personality.
                </span>
              </div>
              <div
                class="demo-pane hover-pane"
                hidden={motionTab.value !== "hover"}
              >
                <div class="demo-grid" />
                <Motion.button
                  class="interaction-square"
                  hover={{ scale: 1.12, rotate: -8 }}
                  press={{ scale: 0.92, rotate: 4 }}
                  focus={{ scale: 1.08 }}
                  transition={{ duration: 320 }}
                  aria-label="hello. Try hover and press animation"
                >
                  <span>hello.</span>
                  <Arrow />
                </Motion.button>
                <span class="demo-instruction">
                  Hover. Press. Make yourself at home.
                </span>
              </div>
              <div
                class="demo-pane scroll-pane"
                hidden={motionTab.value !== "scroll"}
              >
                <div class="demo-grid" />
                <div class="scroll-preview">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <span class="demo-instruction">
                  Scroll the page. Watch the layers respond.
                </span>
              </div>
              <div class="demo-controls">
                <label for="stiffness">
                  Stiffness <strong>{stiffness.value}</strong>
                </label>
                <input
                  id="stiffness"
                  aria-label="Spring stiffness"
                  type="range"
                  min="60"
                  max="320"
                  step="10"
                  value={stiffness.value}
                  onInput={(event: Event) =>
                    (stiffness.value = Number(
                      (event.target as HTMLInputElement).value,
                    ))
                  }
                />
                <button
                  class="replay-button"
                  onClick={() => {
                    motionTab.value = "spring";
                    replaySpring();
                  }}
                >
                  Replay spring <span>↻</span>
                </button>
              </div>
              <div class="demo-code">
                <span class="code-purple">spring</span>
                <span>
                  (0, target, update, {"{"} stiffness: {stiffness.value} {"}"})
                </span>
              </div>
            </div>
          </div>
        </section>

        <section
          id="scroll-experience"
          class="scroll-scene"
          aria-labelledby="scroll-heading"
        >
          <div class="scene-sticky section-wrap">
            <div class="section-top">
              <span class="eyebrow">
                <span class="status-dot" /> FIND YOUR FLOW
              </span>
              <span class="section-number">
                SCROLL PROGRESS <span data-scroll-percentage="true">00%</span>
              </span>
            </div>
            <div class="scene-heading">
              <h2 id="scroll-heading">
                One smooth
                <br />
                <span class="serif-word">experience.</span>
              </h2>
              <p>
                From the first interaction
                <br />
                to the very last pixel.
              </p>
            </div>
            <div class="layer-stack" aria-hidden="true">
              <div class="stack-layer layer-back">
                <span>03 — DURABLE DATA</span>
                <strong>Always with you.</strong>
                <span class="stack-symbol">◫</span>
              </div>
              <div class="stack-layer layer-middle">
                <span>02 — NATIVE MOTION</span>
                <strong>Made to move.</strong>
                <span class="stack-symbol">↝</span>
              </div>
              <div class="stack-layer layer-front">
                <span>01 — REACTIVE CORE</span>
                <strong>
                  Less work.
                  <br />
                  More possibility.
                </strong>
                <span class="stack-symbol">✳</span>
                <span class="layer-footer">
                  ONE COHERENT MODEL <span>↗</span>
                </span>
              </div>
            </div>
            <div class="scene-bottom">
              <span>ZYRA SCROLL / POWERED BY LENIS</span>
              <span class="scene-track">
                <span />
              </span>
              <span>KEEP EXPLORING ↓</span>
            </div>
          </div>
        </section>

        <section id="playground" class="playground-section section-wrap">
          <div class="section-top reveal">
            <span class="eyebrow">
              <span class="status-dot" /> REAL CODE. REAL STATE.
            </span>
            <span class="section-number">03 / THE PLAYGROUND</span>
          </div>
          <div class="section-intro reveal">
            <h2>
              Less theory.
              <br />
              <span class="muted">More try it yourself.</span>
            </h2>
            <p>
              This list runs on Zyra. Add an idea,
              <br />
              take it offline, and come back to it.
              <br />
              Your state stays right here.
            </p>
          </div>
          <div
            class="workspace-tabs reveal"
            role="group"
            aria-label="Playground view"
          >
            <button
              aria-pressed={tab.value === "workspace"}
              onClick={() => (tab.value = "workspace")}
            >
              Live playground <span class="status-dot" />
            </button>
            <button
              aria-pressed={tab.value === "source"}
              onClick={() => (tab.value = "source")}
            >
              Under the hood <span>{"</>"}</span>
            </button>
            <span class="playground-file">examples / offline-tasks</span>
          </div>
          <Show when={tab.value === "workspace"}>
            <div class="workspace-grid">
              <section class="task-panel" aria-labelledby="tasks-heading">
                <div class="panel-heading">
                  <div>
                    <span class="eyebrow">YOUR NEXT GREAT IDEA</span>
                    <h3 id="tasks-heading">
                      Make room for it. <span>{tasks.items.length}</span>
                    </h3>
                  </div>
                  <span class="save-label">
                    <span class="status-dot" />
                    {tasks.status.storageDegraded
                      ? "Storage unavailable"
                      : "Saved on this device"}
                  </span>
                </div>
                <form class="add-form" onSubmit={addTask}>
                  <span class="plus">＋</span>
                  <input
                    aria-label="New task"
                    placeholder="What will you make next?"
                    maxlength="200"
                    value={draft.value}
                    onInput={(event: Event) =>
                      (draft.value = (event.target as HTMLInputElement).value)
                    }
                  />
                  <button
                    type="submit"
                    disabled={
                      busy.value ||
                      !tasks.status.writable ||
                      !draft.value.trim()
                    }
                  >
                    Add task <span>↵</span>
                  </button>
                </form>
                <div class="list-toolbar">
                  <div class="filters" role="group" aria-label="Filter tasks">
                    <button
                      class={filter.value === "all" ? "selected" : ""}
                      onClick={() => (filter.value = "all")}
                    >
                      All tasks
                    </button>
                    <button
                      class={filter.value === "open" ? "selected" : ""}
                      onClick={() => (filter.value = "open")}
                    >
                      In progress
                    </button>
                    <button
                      class={filter.value === "done" ? "selected" : ""}
                      onClick={() => (filter.value = "done")}
                    >
                      Completed
                    </button>
                  </div>
                  <span class="list-count">{visible.value.length} IDEAS</span>
                </div>
                <div class="task-list">
                  <For each={visible.value} key="id">
                    {(task: Task) => (
                      <Motion.article
                        class={task.done ? "task-row done" : "task-row"}
                        enter={{ opacity: 0, y: 12 }}
                        transition={{ duration: 250 }}
                      >
                        <input
                          class="task-checkbox"
                          type="checkbox"
                          aria-label={`Complete ${task.title}`}
                          checked={task.done}
                          onChange={() =>
                            void action(() =>
                              tasks.update(task.id, { done: !task.done }),
                            )
                          }
                        />
                        <div class="task-content">
                          <input
                            class="task-title"
                            aria-label="Task title"
                            value={task.title}
                            maxlength="200"
                            onChange={(event: Event) =>
                              void action(() =>
                                tasks.update(task.id, {
                                  title: (
                                    event.target as HTMLInputElement
                                  ).value.trim(),
                                }),
                              )
                            }
                          />
                          <span class="task-category">{task.category}</span>
                        </div>
                        <button
                          class="delete-task"
                          aria-label={`Delete ${task.title}`}
                          onClick={() =>
                            void action(() => tasks.remove(task.id))
                          }
                        >
                          ×
                        </button>
                      </Motion.article>
                    )}
                  </For>
                  <Show when={!visible.value.length}>
                    <div class="empty-state">
                      <span>✳</span>
                      <h3>Space for something good.</h3>
                      <p>
                        {filter.value === "all"
                          ? "Add your first idea above."
                          : "No tasks in this view. Try another filter."}
                      </p>
                    </div>
                  </Show>
                </div>
                <div class="list-footer" aria-live="polite">
                  <span>
                    {tasks.items.length - completed.value} ideas in progress
                  </span>
                  <span>{completed.value} completed</span>
                </div>
              </section>
              <aside class="inspector">
                <section class="runtime-card">
                  <div class="card-label">
                    UNDER THE SURFACE <span class="status-dot" />
                  </div>
                  <h3>
                    Only what
                    <br />
                    changes, changes.
                  </h3>
                  <p>
                    The list updates.
                    <br />
                    Component setup stays at one.
                  </p>
                  <div class="metric">
                    <span>Component setups</span>
                    <strong data-testid="setup-count">
                      {setupRuns}
                      <small>run</small>
                    </strong>
                  </div>
                  <div class="metric">
                    <span>Local records</span>
                    <strong>
                      {tasks.items.length}
                      <small>items</small>
                    </strong>
                  </div>
                  <div class="metric">
                    <span>Pending mutations</span>
                    <strong>
                      {tasks.status.pending}
                      <small>queued</small>
                    </strong>
                  </div>
                  <div class="runtime-bottom">
                    <span class="status-dot" /> FINE-GRAINED RENDERING
                  </div>
                </section>
                <section class="connection-card">
                  <div class="connection-heading">
                    <span
                      class={
                        online.value ? "status-dot" : "status-dot offline-dot"
                      }
                    />
                    <strong>
                      {online.value
                        ? "Online. Local first."
                        : "Offline. Still yours."}
                    </strong>
                  </div>
                  <p>
                    This playground saves on your device. Learn how owner-scoped
                    collections connect to your own backend.
                  </p>
                  <router.Link
                    class="connect-button"
                    href="/docs/synchronization"
                  >
                    Read the sync guide
                    <Arrow />
                  </router.Link>
                </section>
              </aside>
            </div>
          </Show>
          <Show when={tab.value === "source"}>
            <section class="source-panel">
              <div class="source-heading">
                <h3>A framework you can read.</h3>
                <span>
                  App.tsx <span class="status-dot" />
                </span>
              </div>
              <pre data-lenis-prevent="true">
                <code>{source}</code>
              </pre>
              <p>
                TSX becomes direct DOM bindings. Data and its mutation journal
                commit together in IndexedDB.
              </p>
            </section>
          </Show>
          <Show when={tasks.status.storageDegraded}>
            <p class="alert" role="status">
              Persistent storage is unavailable. Durable changes are blocked
              until storage recovers. {tasks.status.error}
            </p>
          </Show>
          <Show when={message.value || tasks.status.error}>
            <p class="alert" role="alert">
              {message.value || tasks.status.error}
            </p>
          </Show>
          <div class="offline-tip">
            <span>↳</span>
            <p>
              Go offline. Add an idea. Refresh.
              <strong> It's still your idea.</strong>
            </p>
            <span>THAT'S THE POINT.</span>
          </div>
        </section>

        <section id="get-started" class="closing-section section-wrap">
          <div class="closing-top reveal">
            <span class="eyebrow">
              <span class="status-dot" /> YOUR NEXT CHAPTER
            </span>
            <span class="preview-badge">OPEN DEVELOPMENT PREVIEW</span>
          </div>
          <h2 class="reveal">
            Make something
            <br />
            <span class="serif-word">worth feeling.</span>
          </h2>
          <div class="closing-bottom reveal">
            <p>
              The foundation is here.
              <br />
              What happens next is up to you.
            </p>
            <router.Link class="button landing-docs-cta" href="/docs">
              Read the documentation <Arrow />
            </router.Link>
            <button
              class="command-button"
              onClick={() => void copyCommand()}
              title="Copy development command"
            >
              <span class="code-muted">$</span> npm run dev{" "}
              <span>{copied.value ? "Copied ✓" : "Copy ↗"}</span>
            </button>
          </div>
        </section>
      </main>
      <footer class="site-footer section-wrap">
        <a href="#" aria-label="Zyra home">
          <Logo />
        </a>
        <span>Render precisely. Work offline. Move beautifully.</span>
        <router.Link href="/docs/ui">Explore Zyra UI ↗</router.Link>
        <a href="#hero-title">Back to top ↑</a>
        <span class="footer-version">ZYRA © 2026 · v0.1</span>
      </footer>
    </div>
  );
}
